import { isEqual } from "lodash";
import { ProcessoSetorModel } from "../model/api/processo-setor/processo-setor-model";
import { SetorProcessoModel } from "../model/api/processo-setor/setor-processo/setor-processo-model";

type ValidarProcessoFn = (processo: SetorProcessoModel) => boolean;

export interface SincronizacaoProcessosSetoresResultado {
  processosSetores: ProcessoSetorModel[];
  teveAlteracao: boolean;
  setoresRemovidos: number;
  processosRemovidos: number;
  setoresAtualizados: number;
  processosAtualizados: number;
}

const obterChaveSetor = (setor: ProcessoSetorModel) => {
  return `${setor.setor?.id ?? ""}::${setor.salao?.id ?? ""}`;
};

const obterIdsOrdenados = (processos: SetorProcessoModel[]) => {
  return processos.map((processo) => processo.id).sort();
};

export const sincronizarProcessosSetoresStorage = (
  setoresStorage: ProcessoSetorModel[],
  setoresRetaguarda: ProcessoSetorModel[],
  validarProcesso?: ValidarProcessoFn,
): SincronizacaoProcessosSetoresResultado => {
  const resultado: SincronizacaoProcessosSetoresResultado = {
    processosSetores: [],
    teveAlteracao: false,
    setoresRemovidos: 0,
    processosRemovidos: 0,
    setoresAtualizados: 0,
    processosAtualizados: 0,
  };

  if (!Array.isArray(setoresStorage) || setoresStorage.length === 0) {
    return resultado;
  }

  const setoresRetaguardaMap = new Map<string, ProcessoSetorModel>();
  setoresRetaguarda.forEach((setor) => {
    setoresRetaguardaMap.set(obterChaveSetor(setor), setor);
  });

  setoresStorage.forEach((setorStorage) => {
    const setorRetaguarda = setoresRetaguardaMap.get(obterChaveSetor(setorStorage));
    if (!setorRetaguarda) {
      resultado.setoresRemovidos += 1;
      return;
    }

    const processosRetaguardaFiltrados = (setorRetaguarda.processos ?? []).filter((processo) =>
      validarProcesso ? validarProcesso(processo) : true,
    );
    const processosRetaguardaMap = new Map<string, SetorProcessoModel>();
    processosRetaguardaFiltrados.forEach((processo) => processosRetaguardaMap.set(processo.id, processo));

    const processosSincronizados: SetorProcessoModel[] = [];

    (setorStorage.processos ?? []).forEach((processoStorage) => {
      const processoAtual = processosRetaguardaMap.get(processoStorage.id);
      if (!processoAtual) {
        resultado.processosRemovidos += 1;
        return;
      }

      if (
        processoStorage.descricao !== processoAtual.descricao ||
        processoStorage.processoProducao !== processoAtual.processoProducao ||
        processoStorage.status?.codigo !== processoAtual.status?.codigo
      ) {
        resultado.processosAtualizados += 1;
      }

      processosSincronizados.push(processoAtual);
    });

    if (processosSincronizados.length < 1) {
      resultado.setoresRemovidos += 1;
      if ((setorStorage.processos ?? []).length > 0) {
        resultado.processosRemovidos += setorStorage.processos.length;
      }
      return;
    }

    if (
      setorStorage.setor?.descricao !== setorRetaguarda.setor?.descricao ||
      setorStorage.salao?.descricao !== setorRetaguarda.salao?.descricao
    ) {
      resultado.setoresAtualizados += 1;
    }

    const setorSincronizado: ProcessoSetorModel = {
      ...setorRetaguarda,
      processos: processosSincronizados,
    };

    resultado.processosSetores.push(setorSincronizado);

    if (
      !isEqual(
        obterIdsOrdenados(setorStorage.processos ?? []),
        obterIdsOrdenados(processosSincronizados),
      )
    ) {
      resultado.teveAlteracao = true;
    }
  });

  resultado.teveAlteracao =
    resultado.teveAlteracao ||
    resultado.setoresRemovidos > 0 ||
    resultado.processosRemovidos > 0 ||
    resultado.setoresAtualizados > 0 ||
    resultado.processosAtualizados > 0 ||
    resultado.processosSetores.length !== setoresStorage.length;

  return resultado;
};
